import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import SubmitButton from '@common_components/buttons/submitButton.component';
import { TrashIcon } from '@common_components/icons/trash.icon';
import { GraphicUnitSuffixInput } from 'components/inputs/graphicUnitSuffixInput.component';

interface GraphicUnitSectionProps {
  graphicUnitSuffix: string;
  setGraphicUnitSuffix: ( value: string ) => void;
  graphicUnits: string[];
  setGraphicUnits: ( updater: ( prev: string[] ) => string[] ) => void;
}

const GraphicUnitSection: React.FC<GraphicUnitSectionProps> = ( {
  graphicUnitSuffix,
  setGraphicUnitSuffix,
  graphicUnits,
  setGraphicUnits,
} ) => {
  const { t } = useTranslation();

  const [ editingIndex, setEditingIndex ] = useState<number | null>( null );
  const [ editingValue, setEditingValue ] = useState<string>( '' );
  const [ deletedGraphicUnits, setDeletedGraphicUnits ] = useState<string[]>( [] );

  return (
    <>
      <h3 className="font-bold mb-3">{ t( 'translation:graphic_unit' ) }</h3>
      <div className="flex flex-col gap-3 max-w-md">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <GraphicUnitSuffixInput
              value={ graphicUnitSuffix }
              onChange={ setGraphicUnitSuffix }
            />
          </div>
          <SubmitButton
            label={ t( 'common:add' ) }
            disable={ graphicUnitSuffix.trim() === '' }
            onClick={ () => {
              const trimmed = graphicUnitSuffix.trim();
              if ( trimmed === '' ) {
                return;
              }
              setGraphicUnits( prev => {
                if ( prev.includes( trimmed ) ) {
                  return prev;
                }
                return [ ...prev, trimmed ];
              } );
              setGraphicUnitSuffix( '' );
            } }
            width="w-auto"
            bgColor={ graphicUnitSuffix.trim() === '' ? 'bg-green-100' : 'bg-green-500' }
            textColor={ graphicUnitSuffix.trim() === '' ? 'text-green-300' : 'text-white' }
            hoverBorderColor={ graphicUnitSuffix.trim() === '' ? '' : 'group-hover:border-green-600' }
            bgColorHover={ graphicUnitSuffix.trim() === '' ? '' : 'group-hover:bg-green-600' }
            textColorHover={ graphicUnitSuffix.trim() === '' ? '' : 'group-hover:text-white' }
            textSize="text-xs font-medium"
          />
        </div>

        {graphicUnits.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1">
            {graphicUnits.map( ( suffix, index ) => {return (
              <div
                key={`${suffix}-${index}`}
                className="flex items-center gap-2 px-2 py-1 rounded-md border border-blue-200 bg-blue-50 text-sm"
              >
                {editingIndex === index ? (
                  <>
                    <input
                      type="text"
                      value={ editingValue }
                      onChange={ event => { return setEditingValue( event.target.value ); } }
                      maxLength={45}
                      className="flex-1 border border-blue-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                    />
                    <button
                      type="button"
                      className="text-xs text-gray-600 px-1"
                      onClick={ () => {
                        setEditingIndex( null );
                        setEditingValue( '' );
                      } }
                    >
                      {t( 'common:cancel' )}
                    </button>
                    <button
                      type="button"
                      className="text-xs text-gray-700 px-1 disabled:text-gray-400"
                      disabled={ editingValue.trim() === '' || editingValue.trim() === suffix }
                      onClick={ () => {
                        const trimmed = editingValue.trim();
                        if ( trimmed === '' || trimmed === suffix ) {
                          return;
                        }

                        setGraphicUnits( prev => {
                          if ( prev.some( ( value, valueIndex ) => { return value === trimmed && valueIndex !== index; } ) ) {
                            return prev;
                          }
                          return prev.map( ( value, valueIndex ) => {
                            if ( valueIndex === index ) {
                              return trimmed;
                            }
                            return value;
                          } );
                        } );

                        setEditingIndex( null );
                        setEditingValue( '' );
                      } }
                    >
                      {t( 'common:save' )}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="truncate max-w-[160px] text-left"
                      onClick={ () => {
                        setEditingIndex( index );
                        setEditingValue( suffix );
                      } }
                    >
                      {suffix}
                    </button>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700 text-[10px] font-semibold flex items-center justify-center"
                      onClick={ () => {
                        const removedValue = suffix;
                        setGraphicUnits( prev => {
                          return prev.filter( ( _item, itemIndex ) => { return itemIndex !== index; } );
                        } );
                        setDeletedGraphicUnits( prev => {
                          if ( prev.includes( removedValue ) ) {
                            return prev;
                          }
                          return [ ...prev, removedValue ];
                        } );
                      } }
                    >
                      <TrashIcon className="w-[20px] h-[20px]" />
                    </button>
                  </>
                )}
              </div>
            );} )}
          </div>
        )}
        {deletedGraphicUnits.length > 0 && (
          <div className="mt-2 flex flex-col gap-1">
            <p className="text-[11px] text-gray-500 font-medium">
              {t( 'translation:deleted_graphic_units' )}
            </p>
            <div className="flex flex-wrap gap-2">
              {deletedGraphicUnits.map( unit => {return (
                <button
                  key={unit}
                  type="button"
                  className="px-2 py-0.5 rounded-full border border-dashed border-gray-300 text-xs text-gray-500 bg-gray-50 hover:bg-gray-100"
                  onClick={ () => {
                    setGraphicUnits( prev => {
                      if ( prev.includes( unit ) ) {
                        return prev;
                      }
                      return [ ...prev, unit ];
                    } );
                    setDeletedGraphicUnits( prev => {
                      return prev.filter( value => { return value !== unit; } );
                    } );
                  } }
                >
                  {unit}
                </button>
              );} )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GraphicUnitSection;
